// src/components/digital_twin/twin_engine/ui/AlarmsTab.js
// Open alerts on the asset and its sensors, with acknowledge / resolve written back to the
// backend (ActiveAlertService) and an immediate refetch.
import React, { memo, useState } from 'react';
import { Box, Button, Flex, HStack, Input, Text, VStack, useToast } from '@chakra-ui/react';
import { FaBell, FaCheck } from 'react-icons/fa';
import { Empty, LevelBadge, ago, useTwinTokens } from './ui';
import { ackAlert, resolveAlert } from '../twinData';

const SEV = { critical: 'critical', high: 'high', warning: 'watch', medium: 'watch', low: 'info', info: 'info' };

function AlarmsTab({ alerts, source, isLive, onChanged }) {
  const t = useTwinTokens();
  const toast = useToast();
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState({});

  const act = async (a, kind) => {
    setBusy(`${a.id}:${kind}`);
    try {
      if (kind === 'ack') await ackAlert(a.id); else await resolveAlert(a.id, notes[a.id]);
      toast({ status: 'success', title: kind === 'ack' ? 'Alert acknowledged' : 'Alert resolved', duration: 2500, position: 'top-right' });
      onChanged?.();
    } catch (e) {
      toast({ status: 'error', title: 'Update failed', description: e?.response?.data?.message || e.message, duration: 5000, position: 'top-right' });
    } finally { setBusy(null); }
  };

  if (source?.status === 'error') return <Empty icon={FaBell} title="Alerts unavailable">{source.error}</Empty>;
  if (!alerts.length) return <Empty icon={FaCheck} title="No open alerts">Nothing is alarming on this asset or its linked sensors.</Empty>;
  return (
    <VStack align="stretch" spacing={3}>
      {alerts.map((a) => (
        <Box key={a.id} bg={t.card} borderWidth="1px" borderColor={t.border} borderLeftWidth="4px" borderLeftColor={a.severity === 'critical' ? 'red.400' : 'orange.400'} borderRadius="lg" p={3}>
          <Flex justify="space-between" gap={2} align="flex-start">
            <Box minW={0}>
              <HStack spacing={2} mb={1}><LevelBadge level={SEV[a.severity] || 'watch'}>{a.severity}</LevelBadge><LevelBadge level={a.status === 'acknowledged' ? 'info' : 'high'}>{a.status}</LevelBadge></HStack>
              <Text fontSize="14px" fontWeight="700">{a.message}</Text>
              <Text fontSize="11px" color={t.muted}>{a.createdAt ? ago(a.createdAt) : ''}{a.sensorId ? ` · sensor ${a.sensorId}` : ''}{a.workOrderId ? ` · WO ${a.workOrderId}` : ' · no work order'}</Text>
            </Box>
          </Flex>
          {isLive && (
            <HStack mt={2} spacing={2}>
              <Input size="sm" placeholder="Resolution note (optional)" value={notes[a.id] || ''} onChange={(e) => setNotes((n) => ({ ...n, [a.id]: e.target.value }))} />
              {a.status !== 'acknowledged' && <Button size="sm" variant="outline" isLoading={busy === `${a.id}:ack`} onClick={() => act(a, 'ack')}>Acknowledge</Button>}
              <Button size="sm" colorScheme="green" isLoading={busy === `${a.id}:res`} onClick={() => act(a, 'res')}>Resolve</Button>
            </HStack>
          )}
        </Box>
      ))}
    </VStack>
  );
}

export default memo(AlarmsTab);
